// Paramètres Acheteur - Page pour les ACHETEURS
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Settings, ArrowLeft, Bell, Mail, Shield, Globe, Moon, Sun,
  Smartphone, Lock, Eye, EyeOff, ChevronRight, Check, Volume2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

import { API_URL } from '../../config/api';

// Toggle Switch
function Toggle({ enabled, onChange }) {
  return (
    <button
      onClick={() => onChange(!enabled)}
      className={`relative w-12 h-6 rounded-full transition-colors ${
        enabled ? 'bg-orange-500' : 'bg-gray-300'
      }`}
    >
      <motion.div
        initial={false}
        animate={{ x: enabled ? 24 : 2 }}
        className="w-5 h-5 bg-white rounded-full shadow-md absolute top-0.5"
      />
    </button>
  );
}

// Section de paramètres
function SettingsSection({ title, children }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-4">
      <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
        <h3 className="font-semibold text-gray-900">{title}</h3>
      </div>
      <div className="divide-y divide-gray-100">{children}</div>
    </div>
  );
}

// Ligne de paramètre
function SettingRow({ icon: Icon, label, description, children }) {
  return (
    <div className="px-4 py-4 flex items-center gap-4">
      <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0">
        <Icon size={20} className="text-gray-600" />
      </div>
      <div className="flex-1">
        <p className="font-medium text-gray-900">{label}</p>
        {description && <p className="text-sm text-gray-500">{description}</p>}
      </div>
      {children}
    </div>
  );
}

export default function ParametresPage() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    // Notifications
    emailPromos: true,
    emailOrders: true,
    pushNotifications: true,
    smsAlerts: false,
    // Préférences
    darkMode: false,
    language: 'fr',
    // Confidentialité
    showProfile: true,
    showActivity: false,
  });

  const updateSetting = (key, value) => {
    setSettings({ ...settings, [key]: value });
    toast.success('Paramètre mis à jour');
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="parametres-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-lg">
              <ArrowLeft size={20} className="text-gray-600" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <Settings size={24} className="text-gray-600" />
                Paramètres
              </h1>
              <p className="text-sm text-gray-500">Gère tes préférences</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        {/* Notifications */}
        <SettingsSection title="Notifications">
          <SettingRow
            icon={Mail}
            label="Promotions par email"
            description="Recevez nos meilleures offres"
          >
            <Toggle
              enabled={settings.emailPromos}
              onChange={(v) => updateSetting('emailPromos', v)}
            />
          </SettingRow>
          <SettingRow
            icon={Mail}
            label="Suivi de commande"
            description="Mises à jour sur tes commandes"
          >
            <Toggle
              enabled={settings.emailOrders}
              onChange={(v) => updateSetting('emailOrders', v)}
            />
          </SettingRow>
          <SettingRow
            icon={Bell}
            label="Notifications push"
            description="Alertes en temps réel"
          >
            <Toggle
              enabled={settings.pushNotifications}
              onChange={(v) => updateSetting('pushNotifications', v)}
            />
          </SettingRow>
          <SettingRow
            icon={Smartphone}
            label="Alertes SMS"
            description="Notifications par SMS"
          >
            <Toggle
              enabled={settings.smsAlerts}
              onChange={(v) => updateSetting('smsAlerts', v)}
            />
          </SettingRow>
        </SettingsSection>

        {/* Préférences */}
        <SettingsSection title="Préférences">
          <SettingRow
            icon={settings.darkMode ? Moon : Sun}
            label="Mode sombre"
            description="Activer le thème sombre"
          >
            <Toggle
              enabled={settings.darkMode}
              onChange={(v) => updateSetting('darkMode', v)}
            />
          </SettingRow>
          <SettingRow
            icon={Globe}
            label="Langue"
            description="Choisir la langue de l'interface"
          >
            <select
              value={settings.language}
              onChange={(e) => updateSetting('language', e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
            >
              <option value="fr">Français</option>
              <option value="en">English</option>
              <option value="de">Deutsch</option>
              <option value="es">Español</option>
            </select>
          </SettingRow>
        </SettingsSection>

        {/* Confidentialité */}
        <SettingsSection title="Confidentialité">
          <SettingRow
            icon={Eye}
            label="Profil visible"
            description="Autres utilisateurs peuvent voir ton profil"
          >
            <Toggle
              enabled={settings.showProfile}
              onChange={(v) => updateSetting('showProfile', v)}
            />
          </SettingRow>
          <SettingRow
            icon={Shield}
            label="Historique d'activité"
            description="Afficher ton activité récente"
          >
            <Toggle
              enabled={settings.showActivity}
              onChange={(v) => updateSetting('showActivity', v)}
            />
          </SettingRow>
        </SettingsSection>

        {/* Sécurité */}
        <SettingsSection title="Sécurité">
          <button
            onClick={() => toast.info('Fonctionnalité à venir')}
            className="w-full px-4 py-4 flex items-center gap-4 hover:bg-gray-50"
          >
            <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center">
              <Lock size={20} className="text-gray-600" />
            </div>
            <div className="flex-1 text-left">
              <p className="font-medium text-gray-900">Changer le mot de passe</p>
              <p className="text-sm text-gray-500">Modifier ton mot de passe</p>
            </div>
            <ChevronRight size={20} className="text-gray-400" />
          </button>
        </SettingsSection>

        {/* Compte */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <Button
            variant="outline"
            className="w-full text-red-600 border-red-200 hover:bg-red-50 rounded-xl"
            onClick={() => toast.info('Déconnexion...')}
          >
            Se déconnecter
          </Button>
        </div>

        <p className="text-center text-sm text-gray-400 mt-6">
          Version 1.0.0 • AbracadaBati
        </p>
      </main>
    </div>
  );
}
